import { lazy } from "react";
import type { ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router";
import type { RouteObject } from "react-router";

import { ADMIN_CREATE, EMPLOYEE_CREATE } from "@/app/create";
import { AppShell } from "@/app/layouts/AppShell";
import { AuthLayout } from "@/app/layouts/AuthLayout";
import { OnboardingLayout } from "@/app/layouts/OnboardingLayout";
import { ADMIN_NAV, EMPLOYEE_NAV } from "@/app/nav";
import {
  RedirectIfSignedIn,
  RequireActive,
  RequireAdmin,
  RequireAuth,
  RequireManager,
  RequirePayrollChain,
} from "@/app/guards/RouteGuards";
import { AccountMenu } from "@/app/layouts/AccountMenu";
import { ScreenBoundary, ScreenError } from "@/app/ScreenBoundary";

// What arrives with the first file, beside the frames and the guards: the
// screens somebody lands on. Signing in, then the first screen of each role.
// The two of those are about sixteen kilobytes over the wire between them,
// and fetched apart they would cost everybody a second round trip and a
// placeholder on the way in, which is more than they weigh. And the two
// status pages, which have no frame around them for a placeholder to sit
// in. Everything else is fetched when it is opened.
import { AccountStatusRoutes } from "@/routes/AccountStatus";
import { DashboardRoute } from "@/routes/admin/Dashboard";
import { SignInRoute } from "@/routes/auth/SignIn";
import { HomeRoute } from "@/routes/Home";

const { PendingRoute, RejectedRoute } = AccountStatusRoutes;

/** The names in a route file that are screens, as opposed to anything else it exports. */
type ScreenIn<M> = {
  [K in keyof M]: M[K] extends ComponentType ? K : never;
}[keyof M];

/**
 * A screen whose code is fetched the first time somebody opens it.
 *
 * Route files export their screens by name and `lazy` wants a default, so
 * this picks the named one out. Two screens from one file are one fetch.
 */
function screen<M>(load: () => Promise<M>, name: ScreenIn<M>) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const RegisterRoute = screen(() => import("@/routes/auth/Register"), "RegisterRoute");
const ForgotPasswordRoute = screen(() => import("@/routes/auth/ForgotPassword"), "ForgotPasswordRoute");
const ResetPasswordRoute = screen(() => import("@/routes/auth/ResetPassword"), "ResetPasswordRoute");
const ChoosePasswordRoute = screen(() => import("@/routes/auth/ChoosePassword"), "ChoosePasswordRoute");

const OnboardingStartRoute = screen(() => import("@/routes/OnboardingStart"), "OnboardingStartRoute");
const OnboardingRoute = screen(() => import("@/routes/Onboarding"), "OnboardingRoute");

const ProfileRoute = screen(() => import("@/routes/Profile"), "ProfileRoute");
const CorrectionsRoute = screen(() => import("@/routes/Corrections"), "CorrectionsRoute");
const LeavesRoute = screen(() => import("@/routes/Leaves"), "LeavesRoute");
const RequestLeaveRoute = screen(() => import("@/routes/RequestLeave"), "RequestLeaveRoute");
const MyTeamKpiRoute = screen(() => import("@/routes/WriteKpi"), "MyTeamKpiRoute");
const KpiFormRoute = screen(() => import("@/routes/WriteKpi"), "KpiFormRoute");
const MyKpiRoute = screen(() => import("@/features/kpi/Performance"), "MyKpiRoute");
const OvertimeRoute = screen(() => import("@/routes/Overtime"), "OvertimeRoute");
const MyRotaRoute = screen(() => import("@/routes/Rota"), "MyRotaRoute");
const BreaksRoute = screen(() => import("@/routes/Breaks"), "BreaksRoute");
const RemindersRoute = screen(() => import("@/routes/Reminders"), "RemindersRoute");
const MyWorkRoute = screen(() => import("@/routes/MyWork"), "MyWorkRoute");
const ChatRoute = screen(() => import("@/routes/Chat"), "ChatRoute");
const LeavingRoute = screen(() => import("@/routes/Leaving"), "LeavingRoute");
const ExitInterviewRoute = screen(() => import("@/routes/ExitInterview"), "ExitInterviewRoute");
const ScheduleRoute = screen(() => import("@/routes/Schedule"), "ScheduleRoute");
const DocumentsRoute = screen(() => import("@/routes/Documents"), "DocumentsRoute");
const SignDocumentRoute = screen(() => import("@/routes/SignDocument"), "SignDocumentRoute");

const EmployeeListRoute = screen(() => import("@/routes/admin/EmployeeList"), "EmployeeListRoute");
const EmployeeDetailRoute = screen(() => import("@/routes/admin/EmployeeDetail"), "EmployeeDetailRoute");
const EmployeeReviewRoute = screen(() => import("@/routes/admin/EmployeeReview"), "EmployeeReviewRoute");
const ChangeRequestsRoute = screen(() => import("@/routes/admin/ChangeRequests"), "ChangeRequestsRoute");
const KpiBoardRoute = screen(() => import("@/routes/admin/KpiBoard"), "KpiBoardRoute");
const KpiReviewRoute = screen(() => import("@/routes/admin/KpiBoard"), "KpiReviewRoute");
const KpiLinesRoute = screen(() => import("@/routes/admin/KpiLines"), "KpiLinesRoute");
const LeaveQueueRoute = screen(() => import("@/routes/admin/LeaveQueue"), "LeaveQueueRoute");
const AdminLeavingRoute = screen(() => import("@/routes/admin/Leaving"), "AdminLeavingRoute");
const WorkRoute = screen(() => import("@/routes/admin/Work"), "WorkRoute");
const LettersRoute = screen(() => import("@/routes/admin/Letters"), "LettersRoute");
const SalaryRoute = screen(() => import("@/routes/admin/Salary"), "SalaryRoute");
const PayrollRoute = screen(() => import("@/routes/admin/Payroll"), "PayrollRoute");
const SalarySheetRoute = screen(() => import("@/routes/admin/Payroll"), "SalarySheetRoute");
const PayChangesRoute = screen(() => import("@/routes/PayChanges"), "PayChangesRoute");
const OvertimeAdminRoute = screen(() => import("@/routes/admin/OvertimeAdmin"), "OvertimeAdminRoute");
const RotaRoute = screen(() => import("@/routes/admin/Rota"), "RotaRoute");
const BreakBoardRoute = screen(() => import("@/routes/admin/BreakBoard"), "BreakBoardRoute");
const ForecastRoute = screen(() => import("@/routes/admin/Forecast"), "ForecastRoute");
const DocumentListRoute = screen(() => import("@/routes/admin/DocumentList"), "DocumentListRoute");
const PapersRoute = screen(() => import("@/routes/admin/Papers"), "PapersRoute");
const CompanyDocumentsRoute = screen(() => import("@/routes/admin/CompanyDocuments"), "CompanyDocumentsRoute");
const DocumentStatusRoute = screen(() => import("@/routes/admin/DocumentStatus"), "DocumentStatusRoute");
const DocumentBuilderRoute = screen(() => import("@/routes/admin/DocumentBuilder"), "DocumentBuilderRoute");
const WriteDocumentRoute = screen(() => import("@/routes/admin/WriteDocument"), "WriteDocumentRoute");
const SettingsRoute = screen(() => import("@/routes/admin/Settings"), "SettingsRoute");
const PositionsRoute = screen(() => import("@/routes/admin/Positions"), "PositionsRoute");
const DesignSystemRoute = screen(() => import("@/routes/DesignSystem"), "DesignSystemRoute");

/**
 * The screens that share a frame, behind one boundary inside it.
 *
 * While a screen's code is on its way the frame stays put and the page shows
 * a placeholder; if the code cannot be had, the page offers a reload and the
 * frame is still there to navigate with.
 */
function screens(children: RouteObject[]): RouteObject[] {
  return [{ element: <ScreenBoundary />, errorElement: <ScreenError />, children }];
}

/** Signed in, any account status: onboarding and the status pages. */
function AuthedOnly() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

/** Signed in and approved: the working application. */
function ActiveOnly() {
  return (
    <RequireAuth>
      <RequireActive>
        <Outlet />
      </RequireActive>
    </RequireAuth>
  );
}

function AdminOnly() {
  return (
    <RequireAuth>
      <RequireActive>
        <RequireAdmin>
          <Outlet />
        </RequireAdmin>
      </RequireActive>
    </RequireAuth>
  );
}

const routes: RouteObject[] = [
  {
    element: (
      <RedirectIfSignedIn>
        <AuthLayout />
      </RedirectIfSignedIn>
    ),
    children: screens([
      { path: "/login", element: <SignInRoute /> },
      { path: "/register", element: <RegisterRoute /> },
      { path: "/forgot-password", element: <ForgotPasswordRoute /> },
    ]),
  },

  // Not redirected: a link from an email has to work in a browser that is
  // already signed in, whether as this person or as HR trying it for them.
  {
    element: <AuthLayout />,
    children: screens([{ path: "/reset-password", element: <ResetPasswordRoute /> }]),
  },

  // After signing in with a password HR gave out, and nowhere else until a
  // new one is chosen. Outside the app's frame, which would ask the server
  // for things it refuses until then.
  {
    element: (
      <RequireAuth>
        <AuthLayout />
      </RequireAuth>
    ),
    children: screens([{ path: "/choose-password", element: <ChoosePasswordRoute /> }]),
  },

  {
    element: <AuthedOnly />,
    children: [
      {
        path: "/onboarding",
        element: <OnboardingLayout />,
        children: screens([
          { index: true, element: <OnboardingStartRoute /> },
          { path: ":step", element: <OnboardingRoute /> },
        ]),
      },
      { path: "/pending", element: <PendingRoute /> },
      { path: "/rejected", element: <RejectedRoute /> },
    ],
  },

  {
    element: <ActiveOnly />,
    children: [
      {
        element: <AppShell nav={EMPLOYEE_NAV} create={EMPLOYEE_CREATE} utility={<AccountMenu />} />,
        children: screens([
          { path: "/home", element: <HomeRoute /> },
          { path: "/profile", element: <ProfileRoute /> },
          { path: "/profile/corrections", element: <CorrectionsRoute /> },
          { path: "/leaves", element: <LeavesRoute /> },
          { path: "/kpi", element: <MyTeamKpiRoute /> },
          { path: "/kpi/:reviewId", element: <KpiFormRoute /> },
          { path: "/my-kpi", element: <MyKpiRoute /> },
          { path: "/leaves/new", element: <RequestLeaveRoute /> },
          { path: "/overtime", element: <OvertimeRoute /> },
          { path: "/rota", element: <MyRotaRoute /> },
          { path: "/breaks", element: <BreaksRoute /> },
          { path: "/reminders", element: <RemindersRoute /> },
          { path: "/my-work", element: <MyWorkRoute /> },
          { path: "/chat", element: <ChatRoute /> },
          { path: "/leaving", element: <LeavingRoute /> },
          { path: "/leaving/exit-interview", element: <ExitInterviewRoute /> },
          { path: "/schedule", element: <ScheduleRoute /> },
          { path: "/documents", element: <DocumentsRoute /> },
          { path: "/documents/:documentId/sign", element: <SignDocumentRoute /> },
          {
            // HR's own screens for these, reused: a manager is not HR, so
            // they reach them here, among their own pages.
            element: (
              <RequireManager>
                <Outlet />
              </RequireManager>
            ),
            children: [
              { path: "/work", element: <WorkRoute /> },
              { path: "/break-board", element: <BreakBoardRoute /> },
              { path: "/staffing-ahead", element: <ForecastRoute /> },
            ],
          },
          {
            element: (
              <RequirePayrollChain>
                <Outlet />
              </RequirePayrollChain>
            ),
            children: [
              { path: "/payroll", element: <PayrollRoute /> },
              { path: "/payroll/:sheetId", element: <SalarySheetRoute /> },
              { path: "/pay-changes", element: <PayChangesRoute /> },
            ],
          },
        ]),
      },
    ],
  },

  {
    element: <AdminOnly />,
    children: [
      {
        element: <AppShell nav={ADMIN_NAV} create={ADMIN_CREATE} utility={<AccountMenu />} />,
        children: screens([
          { path: "/admin", element: <DashboardRoute /> },
          { path: "/admin/chat", element: <ChatRoute /> },
          { path: "/admin/employees", element: <EmployeeListRoute /> },
          { path: "/admin/employees/:userId", element: <EmployeeDetailRoute /> },
          { path: "/admin/employees/:userId/review", element: <EmployeeReviewRoute /> },
          { path: "/admin/papers", element: <PapersRoute /> },
          { path: "/admin/change-requests", element: <ChangeRequestsRoute /> },
          { path: "/admin/kpi", element: <KpiBoardRoute /> },
          { path: "/admin/kpi/lines", element: <KpiLinesRoute /> },
          { path: "/admin/kpi/:reviewId", element: <KpiReviewRoute /> },
          { path: "/admin/leaves", element: <LeaveQueueRoute /> },
          { path: "/admin/leaving", element: <AdminLeavingRoute /> },
          { path: "/admin/work", element: <WorkRoute /> },
          { path: "/admin/letters", element: <LettersRoute /> },
          { path: "/admin/salary", element: <SalaryRoute /> },
          { path: "/admin/payroll", element: <PayrollRoute /> },
          { path: "/admin/payroll/:sheetId", element: <SalarySheetRoute /> },
          { path: "/admin/pay-changes", element: <PayChangesRoute /> },
          { path: "/admin/overtime", element: <OvertimeAdminRoute /> },
          { path: "/admin/rota", element: <RotaRoute /> },
          { path: "/admin/breaks", element: <BreakBoardRoute /> },
          { path: "/admin/forecast", element: <ForecastRoute /> },
          {
            path: "/admin/staffing",
            element: <Navigate to="/admin/settings?section=staffing" replace />,
          },
          {
            path: "/admin/cover",
            element: <Navigate to="/admin/settings?section=cover" replace />,
          },
          { path: "/admin/documents", element: <DocumentListRoute /> },
          { path: "/admin/documents/write", element: <WriteDocumentRoute /> },
          { path: "/admin/documents/write/:writtenId", element: <WriteDocumentRoute /> },
          { path: "/admin/library", element: <CompanyDocumentsRoute /> },
          { path: "/admin/documents/:documentId", element: <DocumentStatusRoute /> },
          { path: "/admin/documents/:documentId/build", element: <DocumentBuilderRoute /> },
          { path: "/admin/settings", element: <SettingsRoute /> },
          { path: "/admin/positions", element: <PositionsRoute /> },
          {
            path: "/admin/branches",
            element: <Navigate to="/admin/settings?section=branches" replace />,
          },
          {
            path: "/admin/leave-types",
            element: <Navigate to="/admin/settings?section=leave-types" replace />,
          },
          {
            path: "/admin/holidays",
            element: <Navigate to="/admin/settings?section=holidays" replace />,
          },
          {
            path: "/admin/groups",
            element: <Navigate to="/admin/settings?section=groups" replace />,
          },
          { path: "/design", element: <DesignSystemRoute /> },
        ]),
      },
    ],
  },

  { path: "/", element: <Navigate to="/home" replace /> },
  { path: "*", element: <Navigate to="/home" replace /> },
];

// One route above all the others, only to catch what fails outside a page:
// a frame or a guard that throws gets the whole window and the same way out.
export const router = createBrowserRouter([
  { errorElement: <ScreenError whole />, children: routes },
]);
